import type {
  ErosionInvestigationConfig,
  ErosionTestConditionId,
} from './ErosionInvestigationConfig.js';
import {
  ErosionInvestigationState,
  type ErosionComparisonResult,
  type ErosionInvestigationSnapshot,
  type ErosionTestRecord,
} from './ErosionInvestigationState.js';

export interface ErosionInvestigationEvents {
  onStateChanged?: (snapshot: ErosionInvestigationSnapshot) => void;
  onTestRun?: (record: ErosionTestRecord, snapshot: ErosionInvestigationSnapshot) => void;
  onCompleted?: (
    result: ErosionComparisonResult,
    config: ErosionInvestigationConfig,
    snapshot: ErosionInvestigationSnapshot,
  ) => void;
}

/**
 * UI-independent investigation engine. A Phaser presentation layer can bind
 * production art and input to these commands without embedding curriculum in
 * the scene. Resizing or suspending that presentation does not reset state.
 */
export class ErosionInvestigation {
  private readonly state: ErosionInvestigationState;
  private completionEmitted = false;

  constructor(
    readonly config: ErosionInvestigationConfig,
    private readonly events: ErosionInvestigationEvents = {},
  ) {
    this.state = new ErosionInvestigationState(config);
  }

  getSnapshot() { return this.state.snapshot(); }

  acknowledgeObservation() {
    const changed = this.state.acknowledgeObservation();
    if (changed) this.emitState();
    return changed;
  }

  choosePrediction(predictionId: string) {
    const changed = this.state.choosePrediction(predictionId);
    if (changed) this.emitState();
    return changed;
  }

  selectCondition(conditionId: ErosionTestConditionId) {
    const changed = this.state.selectCondition(conditionId);
    if (changed) this.emitState();
    return changed;
  }

  runSelectedCondition() {
    const record = this.state.runSelectedCondition();
    if (!record) return undefined;
    const snapshot = this.state.snapshot();
    this.events.onTestRun?.(record, snapshot);
    this.events.onStateChanged?.(snapshot);
    return record;
  }

  compareResults() {
    const result = this.state.compareResults();
    if (!result) return undefined;
    const snapshot = this.state.snapshot();
    this.events.onStateChanged?.(snapshot);
    if (!this.completionEmitted) {
      this.completionEmitted = true;
      this.events.onCompleted?.(result, this.config, snapshot);
    }
    return result;
  }

  reset() {
    this.state.reset();
    this.completionEmitted = false;
    this.emitState();
  }

  private emitState() {
    this.events.onStateChanged?.(this.state.snapshot());
  }
}
